/**
 * Routing.
 *
 * Two builds from one source tree, chosen by `VITE_CONSOLE_SURFACE` (see
 * `vite.config.ts`). The client build mounts the client sections only, so a
 * client user never downloads the master console's code — Vite substitutes the
 * constant, the `IS_MASTER` branch folds away, and the master page imports go
 * with it.
 *
 * The master build mounts **both**. That asymmetry is load-bearing rather than
 * laziness: operator mode renders the client shell, and the operator token is
 * held in memory in that same JS context (`lib/api.ts`). A master console on its
 * own origin without the client shell could not enter a client tenant at all.
 *
 * Section paths match `shells/nav.ts` exactly; if you add a section, add it in
 * both places or the shell test will fail.
 */

import { lazy } from 'react';
import { Route, Routes } from 'react-router';
import { ClientShell } from './shells/ClientShell';
import { MasterShell } from './shells/MasterShell';
import NotFoundPage from './pages/not-found';
import UnavailablePage from './pages/unavailable';
import { isNavDisabled } from './shells/nav';

const OverviewPage = lazy(() => import('./pages/client/overview'));
const ChatPage = lazy(() => import('./pages/client/chat'));
const SessionsPage = lazy(() => import('./pages/client/sessions'));
const ApprovalsPage = lazy(() => import('./pages/client/approvals'));
const AutomationsPage = lazy(() => import('./pages/client/automations'));
const SkillsPage = lazy(() => import('./pages/client/skills'));
const IntegrationsPage = lazy(() => import('./pages/client/integrations'));
const GuardrailsPage = lazy(() => import('./pages/client/guardrails'));
const MemoryPage = lazy(() => import('./pages/client/memory'));
const AuditPage = lazy(() => import('./pages/client/audit'));
const ArtifactsPage = lazy(() => import('./pages/client/artifacts'));
const UsagePage = lazy(() => import('./pages/client/usage'));
const UtilisationPage = lazy(() => import('./pages/client/utilisation'));
const PeoplePage = lazy(() => import('./pages/client/people'));
const AgentSettingsPage = lazy(() => import('./pages/client/agent-settings'));
const TestBenchPage = lazy(() => import('./pages/client/test-bench'));
// One chunk for both Help pages — the index and every article share a lazy
// import, because an article is the index with a different body.
const HelpIndexPage = lazy(() => import('./pages/client/help'));
const HelpArticlePage = lazy(() =>
  import('./pages/client/help').then((m) => ({ default: m.HelpArticlePage })),
);

const FleetPage = lazy(() => import('./pages/master/fleet'));
const OrgsPage = lazy(() => import('./pages/master/orgs'));
const IncidentsPage = lazy(() => import('./pages/master/incidents'));
const ReleasesPage = lazy(() => import('./pages/master/releases'));
const BillingPage = lazy(() => import('./pages/master/billing'));
const MasterSettingsPage = lazy(() => import('./pages/master/settings'));

/**
 * Statically replaced at build time, so this is a compile-time constant and not
 * a runtime check. Tests import `AppRoutes` directly and get the default
 * ('client'), except `routes.master.test.tsx`, which stubs the value.
 */
const IS_MASTER = import.meta.env.VITE_CONSOLE_SURFACE === 'master';

export function AppRoutes() {
  return (
    <Routes>
      {IS_MASTER ? (
        <Route path="/master" element={<MasterShell />}>
          <Route index element={<FleetPage />} />
          <Route path="orgs" element={<OrgsPage />} />
          <Route path="incidents" element={<IncidentsPage />} />
          <Route path="releases" element={<ReleasesPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="settings" element={<MasterSettingsPage />} />
          <Route path="*" element={<NotFoundPage home="/master" />} />
        </Route>
      ) : null}

      <Route path="/" element={<ClientShell />}>
        <Route index element={<OverviewPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="automations" element={<AutomationsPage />} />
        <Route path="skills" element={<SkillsPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="guardrails" element={<GuardrailsPage />} />
        <Route path="memory" element={<MemoryPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="artifacts" element={<ArtifactsPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="utilisation" element={<UtilisationPage />} />
        <Route path="people" element={<PeoplePage />} />
        <Route path="agent-settings" element={<AgentSettingsPage />} />
        {/*
          Switched off in `CLIENT_NAV`, and the route reads that same flag.
          Deriving it means a section cannot end up greyed in the sidebar while
          still reachable by a typed URL — and re-enabling it is one flag rather
          than two edits that have to agree.

          Kept as a route rather than deleted so a bookmark lands somewhere that
          explains itself instead of on a 404 that says nothing is here.
        */}
        <Route
          path="test-bench"
          element={
            isNavDisabled('/test-bench') ? <UnavailablePage nav="/test-bench" /> : <TestBenchPage />
          }
        />
        <Route path="help">
          <Route index element={<HelpIndexPage />} />
          <Route path=":topic" element={<HelpArticlePage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
